/** @jsxImportSource @emotion/react */

import { Form as FinalForm } from 'react-final-form'

import { FC, useMemo, useState } from 'react'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'

import { useMutation } from '@apollo/client'
import { toast } from 'react-toastify'
import Table from 'components/Table'
import BaseModal from 'components/Modals/BaseModal'
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg'
import { PlatformName, RewardTypeEnum } from '__generatedTypes__/globalTypes'
import { findAllUserRewardByAdmin_findAllUserRewardByAdmin_items as Item } from '__generatedTypes__/findAllUserRewardByAdmin'
import { NumberInputField } from 'components/Input'
import { validateRequiredNumber } from 'utils/validation'
import { SelectField } from 'components/Select'
import { findAllPlatforms_findAllPlatforms_items as Platform } from '__generatedTypes__/findAllPlatforms'
import TabsNavigation from 'components/TabsNavigation'
import { TabsItem } from 'types/tabsItem'
import { findOneUser_findOneUser as User } from '__generatedTypes__/findOneUser'
import { updateUserRewardByAdmin, updateUserRewardByAdminVariables } from '__generatedTypes__/updateUserRewardByAdmin'
import { UPDATE_USER_REWARD_BY_ADMIN } from 'queries/users'
import Loader from 'components/Loader'
import { getFullPlatformTitle } from 'components/Layout/Sidebar/PlatformList'
import { MAP_ENUM_TO_TITLE } from 'pagesNew/Achievements/components/Rewards/consts'
import { pluralize } from 'utils/pluralize'

import * as classes from './styles'
import { usePauseTokenUpdate } from './usePauseTokenUpdate'

export type FormValues = {
  platform: {
    id: string
    title: PlatformName
  }
  itemCount: number
}

type Props = {
  user: User
  itemsLoading: boolean
  items: Item[]
  platforms: Platform[]
  selectedPlatform: Platform
  setSelectedPlatform: (platform: Platform) => void
  onSuccessfullSubmit: () => Promise<unknown>
  isReadOnly?: boolean
}

export const ItemsManagement: FC<Props> = ({
  user,
  itemsLoading,
  items,
  platforms,
  selectedPlatform,
  setSelectedPlatform,
  onSuccessfullSubmit,
  isReadOnly,
}) => {
  const [updateUserRewardByAdmin] = useMutation<updateUserRewardByAdmin, updateUserRewardByAdminVariables>(
    UPDATE_USER_REWARD_BY_ADMIN
  )

  const [selectedItem, setSelectedItem] = useState<Item | null>(null)

  const handleOpenEditModal = (item: Item) => {
    setSelectedItem(item)
  }

  const handleCloseEditModal = () => {
    setSelectedItem(null)
  }

  const tabs = useMemo(() => platforms.map((platform) => ({ id: platform.id, title: platform.title })), [platforms])

  const handleTabChange = (tab: TabsItem) => {
    const platform = platforms.find((p) => p.id === tab.id)
    if (platform) {
      setSelectedPlatform(platform)
    }
  }

  const initialValues: FormValues = useMemo(
    () => ({
      platform: {
        id: selectedPlatform.id,
        title: selectedPlatform.title,
      },
      itemCount: selectedItem?.availableCount || 0,
    }),
    [selectedItem]
  )

  const { handleUpdateTokensCount } = usePauseTokenUpdate({ user })

  const handleSubmit = async (values: FormValues) => {
    if (!selectedItem) return

    try {
      if (selectedItem.reward.type === RewardTypeEnum.PauseToken) {
        await handleUpdateTokensCount(values)
      } else {
        await updateUserRewardByAdmin({
          variables: {
            input: {
              id: selectedItem.id,
              availableCount: Number(values.itemCount),
              reward: { id: selectedItem.reward.id },
              user: { id: user.id },
            },
          },
        })
      }
      await onSuccessfullSubmit()
      handleCloseEditModal()

      toast.success('Items updated successfully')
    } catch (error) {
      toast.error('Failed to update items')
    }
  }

  const renderContent = () => {
    if (itemsLoading) return <Loader css={classes.loader} />

    if (!items.length) {
      return (
        <Typography variant="subtitle1" css={classes.noItemsFound} data-testid="no-items">
          No items found
        </Typography>
      )
    }

    return (
      <Table
        css={classes.itemsTable}
        columns={[
          {
            title: 'Item type',
            renderCell: (item) => {
              const { reward } = item
              if (reward.type === RewardTypeEnum.Extension) {
                return `+${reward.value} ${pluralize(reward.value, 'Day', 'Days')} Extension`
              }
              return MAP_ENUM_TO_TITLE[reward.type]
            },
            flex: 3,
          },
          {
            title: 'Items Left',
            renderCell: (item) => <span>{item.availableCount}</span>,
            flex: 1,
          },
          {
            title: 'Items Used',
            renderCell: (item) => <span>{item.usedCount}</span>,
            flex: 1,
          },
          {
            renderCell: (item) => (
              <IconButton
                css={classes.rowEditButton}
                onClick={() => handleOpenEditModal(item)}
                disabled={isReadOnly}
                data-testid={`edit-button-${item.id}`}
              >
                <EditIcon />
              </IconButton>
            ),
            flex: '0 1 29px',
          },
        ]}
        items={items}
        wrapperMinWidth={500}
        wrapperPadding={8}
        data-testid="items"
      />
    )
  }

  return (
    <>
      <div css={classes.itemsTableWrapper} data-testid="items-management">
        <TabsNavigation
          css={classes.platformTabs}
          tabs={tabs}
          value={selectedPlatform}
          onChange={handleTabChange}
          renderTabTitle={(tab) => getFullPlatformTitle(tab.title as PlatformName)}
        />

        {renderContent()}
      </div>
      <BaseModal open={!!selectedItem} onClose={handleCloseEditModal}>
        <FinalForm
          initialValues={initialValues}
          onSubmit={handleSubmit}
          render={({ handleSubmit, submitting, dirty }) => (
            <form css={classes.editModalForm} onSubmit={handleSubmit} data-testid="item-form">
              <Typography css={classes.editModalFormTitle}>Edit Items Remaining</Typography>

              <div css={classes.editModalFields}>
                <SelectField
                  name="platform"
                  label="Platform"
                  disabled
                  items={[
                    {
                      id: selectedPlatform.id,
                      title: selectedPlatform.title,
                    },
                  ]}
                  renderItem={(platform) => getFullPlatformTitle(platform.title)}
                  getItemLabel={(platform) => getFullPlatformTitle(platform.title) ?? platform.title}
                  placeholder="Platform"
                  fullWidth
                  data-testid="platform-select"
                />

                <NumberInputField
                  fullWidth
                  name="itemCount"
                  validate={validateRequiredNumber}
                  placeholder="Number of items remaining"
                  label="Number of items remaining"
                  data-testid="item-count-input"
                />
              </div>

              <div css={classes.editModalActions}>
                <Button
                  variant="outlined"
                  fullWidth
                  size="large"
                  onClick={handleCloseEditModal}
                  data-testid="cancel-button"
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="blue"
                  fullWidth
                  size="large"
                  type="submit"
                  disabled={submitting || !dirty}
                  data-testid="submit-button"
                >
                  Submit
                </Button>
              </div>
            </form>
          )}
        />
      </BaseModal>
    </>
  )
}
